import { useContext, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { CustomToast } from '../toast/CustomToast';
import { Text, Stack, Flex, Box, Avatar } from '@chakra-ui/react';
import BrandButton from '../brandButton/BrandButton';
import { mutateFunction } from '../../libs/apis';
import { UserContext } from '../../context/UserContext';
import AutoResizeTextarea from './AutoResizeTextArea';
import { useLocation } from 'react-router-dom';

const EditComment = ({
  onClose = () => {},
  showCloseButton = true,
  comment = {},
  onEditComment = () => {},
}) => {
  const { mutate, isLoading } = useMutation(mutateFunction);
  const { addToast } = CustomToast();
  const [commentMessage, setCommentMessage] = useState(comment?.text || '');
  const { userInformation } = useContext(UserContext);
  const [isPublic, setIsPublic] = useState('');
  const location = useLocation();

  const textMaxLength = 150;

  const formSubmit = async (e) => {
    e.preventDefault();
    if (isPublic) return;

    if (!commentMessage.trim()) {
      addToast({ message: 'Please add some text' }, 'warning');
      return;
    }

    mutate(
      {
        key: `comment/update/${comment.id}`,
        method: 'put',
        data: {
          text: commentMessage.trim(),
        },
      },
      {
        onSuccess(res) {
          addToast(res);
          onClose();
          setCommentMessage('');
          onEditComment();
        },
        onError(e) {
          addToast({ message: 'Error editing comment' }, 'error');
        },
      },
    );
  };

  useEffect(() => {
    if (location.pathname.includes('public')) {
      setIsPublic(true);
    } else {
      setIsPublic(false);
    }
  }, [location]);

  useEffect(() => {
    if (comment?.text) {
      setCommentMessage(comment.text);
    }
  }, [comment]);

  return (
    <Flex flexDirection="column">
      <Stack spacing={6}>
        <Flex className="items-center gap-2" justify={'center'}>
          <Text
            fontSize={20}
            fontWeight={700}
            color="brand.primary"
            align="center"
          >
            Edit Comment
          </Text>
        </Flex>

        <Text fontSize={14} color="#000000">
          Update your comment.
        </Text>
      </Stack>

      <Box mt={'20px'} className="flex items-start gap-2">
        <Box>
          <Avatar
            src={userInformation?.profile_image}
            name={`${userInformation?.firstName} ${userInformation?.lastName}`}
            size={'md'}
          />
        </Box>
        <Flex
          borderBottomWidth="1px"
          alignItems="center"
          className="w-full rounded-3xl bg-gray-200"
        >
          <form className="w-full" onSubmit={formSubmit}>
            <AutoResizeTextarea
              focusBorderColor="#"
              borderWidth="0px"
              background="transparent"
              variant="outline"
              isRequired={true}
              name="commentMessage"
              value={commentMessage}
              onChange={(e) => setCommentMessage(e.target.value)}
              placeholder="Update your comment..."
              type="commentMessage"
              w={'full'}
              maxLength={textMaxLength}
            />
          </form>
        </Flex>
      </Box>

      <Box className="flex justify-end my-1">
        <Text
          className={`text-xs font-semibold ${commentMessage.length < textMaxLength ? 'text-gray-600' : 'text-red-800'}`}
        >
          {commentMessage.length} / {textMaxLength} characters
        </Text>
      </Box>

      <Flex
        gap={10}
        className={`mt-6 ml-10 items-center ${showCloseButton ? 'justify-between' : 'justify-end'}`}
      >
        {showCloseButton ? (
          <BrandButton text="Cancel" onClick={onClose} />
        ) : null}
        <BrandButton
          isLoading={isLoading}
          text="Update Comment"
          onClick={formSubmit}
          type="submit"
          variant="md"
          bgColor="brand.primary"
          color="#fff"
          fontSize="14px"
          borderRadius={4}
          px={8}
          minWidth="150px"
          _hover={{ background: 'brand.primary' }}
        />
      </Flex>
    </Flex>
  );
};

export default EditComment;
